import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Mail } from "lucide-react";
import { BrandWordmark } from "@/components/BrandWordmark";
import api, { apiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { CreateathonBanner } from "@/components/CreateathonBanner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/contact", form);
      setSent(true);
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] grid-noise flex items-center justify-center px-5 py-12">
      <CreateathonBanner />
      <Seo
        title="Contact & Support"
        description="Get in touch with the Videoileo team. Questions about plans, credits, billing, or your AI video series — we're here to help."
        path="/contact"
      />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center mb-8" data-testid="contact-logo-home">
          <BrandWordmark className="h-14" />
        </Link>

        <div className="border border-white/10 rounded-md bg-[#121212] p-8">
          {sent ? (
            <div className="text-center py-4" data-testid="contact-success">
              <div className="h-12 w-12 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-6 w-6 text-emerald-400" />
              </div>
              <h1 className="font-head text-2xl font-bold">Message sent</h1>
              <p className="text-sm text-zinc-400 mt-2">Thanks for reaching out — we'll get back to you at the email you provided, usually within 1–2 business days.</p>
              <Link to="/" className="inline-flex items-center gap-1.5 text-blue-400 hover:text-blue-300 text-sm mt-6" data-testid="contact-back-home">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to home
              </Link>
            </div>
          ) : (
            <>
              <h1 className="font-head text-2xl font-bold inline-flex items-center gap-2"><Mail className="h-5 w-5 text-blue-400" /> Contact us</h1>
              <p className="text-sm text-zinc-500 mb-6">Questions about plans, credits, billing, or your channel? Send us a note.</p>
              <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
                {/* Honeypot: hidden from humans; bots that fill it are silently dropped. */}
                <input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  value={form.website}
                  onChange={set("website")}
                  style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
                />
                {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2" data-testid="contact-error">{error}</p>}
                <TextField label="Your name" value={form.name} onChange={set("name")} testid="contact-name" />
                <TextField label="Email" type="email" value={form.email} onChange={set("email")} testid="contact-email" />
                <TextField label="Subject" value={form.subject} onChange={set("subject")} testid="contact-subject" required={false} />
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">Message</label>
                  <textarea
                    value={form.message}
                    onChange={set("message")}
                    required
                    rows={5}
                    data-testid="contact-message"
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow resize-none"
                  />
                </div>
                <button disabled={loading} data-testid="contact-submit" className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 py-2.5 rounded-md font-semibold transition-colors">
                  {loading ? "Sending…" : "Send message"}
                </button>
              </form>
              <p className="text-xs text-zinc-600 mt-5 text-center">
                Or email us directly at <a href="mailto:support@videoileo.com" className="text-zinc-400 hover:text-zinc-200 underline">support@videoileo.com</a>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function TextField({ label, type = "text", value, onChange, testid, required = true }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-zinc-500 mb-1.5">{label}</label>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-[#0A0A0A] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-shadow"
      />
    </div>
  );
}
